import { Button, Drawer, FormField, Input, SegmentedControl } from '@ntconsult/aluma-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AllocationTagColumn } from '../hooks/useAllocationTagColumns';
import type {
  AllocationTagAdvancedValues,
  AllocationTagFilterState,
} from '../hooks/useAllocationTagFilters';

const TITLE_ID = 'allocation-tag-advanced-filters-title';
const FORM_ID = 'allocation-tag-advanced-filters';

type ActiveChoice = 'all' | 'yes' | 'no';

const toChoice = (value: boolean | null): ActiveChoice =>
  value === null ? 'all' : value ? 'yes' : 'no';
const fromChoice = (choice: ActiveChoice): boolean | null =>
  choice === 'all' ? null : choice === 'yes';

type AllocationTagFiltersDrawerProps = {
  open: boolean;
  onClose: () => void;
  filterState: AllocationTagFilterState;
  /** Colunas visíveis ("Colunas"): cada uma traz o seu campo, como em Alocações. */
  isColumnVisible: (column: AllocationTagColumn) => boolean;
};

function draftFrom({ filters }: AllocationTagFilterState): AllocationTagAdvancedValues {
  return {
    name: filters.name ?? '',
    color: filters.color ?? '',
    active: filters.active ?? null,
  };
}

/**
 * No molde de `ProjectFiltersDrawer`: rascunho aplicado de uma vez; fechar sem
 * aplicar o descarta. Nome é sempre visível; "Alocações vinculadas" não tem
 * filtro na API. Filtro de coluna escondida segue aplicado e no chip.
 */
export function AllocationTagFiltersDrawer({
  open,
  onClose,
  filterState,
  isColumnVisible,
}: AllocationTagFiltersDrawerProps) {
  const { t } = useTranslation(['tagsAlocacao', 'alocacao', 'common']);
  const [draft, setDraft] = useState(() => draftFrom(filterState));
  const [wasOpen, setWasOpen] = useState(open);

  // Reinicia o rascunho a partir do que está aplicado sempre que o painel abre.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(draftFrom(filterState));
  }

  const apply = () => {
    filterState.applyAdvanced(draft);
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} size="md" ariaLabelledBy={TITLE_ID}>
      <Drawer.Header
        titleId={TITLE_ID}
        dismissLabel={t('common:close')}
        title={t('alocacao:filtros.avancados')}
        subtitle={t('tagsAlocacao:filtros.avancadosDescricao')}
        onClose={onClose}
      />
      <Drawer.Body>
        <form
          id={FORM_ID}
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <FormField label={t('tagsAlocacao:colunas.nome')}>
            <Input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </FormField>
          {isColumnVisible('color') && (
            <FormField label={t('tagsAlocacao:colunas.cor')}>
              <Input
                value={draft.color}
                onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
                placeholder={t('tagsAlocacao:filtros.corPlaceholder')}
              />
            </FormField>
          )}
          {isColumnVisible('active') && (
            <FormField label={t('tagsAlocacao:colunas.ativa')}>
              <SegmentedControl
                aria-label={t('tagsAlocacao:colunas.ativa')}
                value={toChoice(draft.active)}
                onValueChange={(choice) =>
                  setDraft((d) => ({ ...d, active: fromChoice(choice as ActiveChoice) }))
                }
              >
                <SegmentedControl.Item value="all">
                  {t('alocacao:consultas.filtros.todos')}
                </SegmentedControl.Item>
                <SegmentedControl.Item value="yes">{t('common:yes')}</SegmentedControl.Item>
                <SegmentedControl.Item value="no">{t('common:no')}</SegmentedControl.Item>
              </SegmentedControl>
            </FormField>
          )}
        </form>
      </Drawer.Body>
      <Drawer.Footer className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t('alocacao:filtros.cancelar')}
        </Button>
        <Button type="submit" form={FORM_ID}>
          {t('alocacao:filtros.aplicar')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}
